import type {
  AdvancedTriggerSettingsType,
  NotificationFieldChangeSettingsType,
  NotificationTriggerSettingsType,
  NotificationTriggerType,
  RevertedTriggerSettingsType,
} from '@cims-online/shared'
import type {
  NotificationFieldChangeSettingsDraft,
  TriggerFormSubmission,
  TriggerSettingsDrafts,
  TriggerSettingsFormValues,
} from '../types'
import {
  createNotificationReferenceValues,
  labeledValueString,
  labeledValueStrings,
  toNotificationStatusReferences,
  type NotificationReferenceData,
} from '../../../utils/referenceValues'

type AdvancedFromState = AdvancedTriggerSettingsType['from'][number]
type AdvancedToState = AdvancedTriggerSettingsType['to'][number]
type RevertedFromState = RevertedTriggerSettingsType['from'][number]
type RevertedToState = RevertedTriggerSettingsType['to'][number]

function copyFieldChangeSettings(
  value: NotificationFieldChangeSettingsType | null,
  references: ReturnType<typeof createNotificationReferenceValues>
): NotificationFieldChangeSettingsDraft | null {
  if (!value) return null
  return {
    enabled: value.enabled,
    anyFieldsChanged: value.anyFieldIdsChanged.map(references.field),
    allFieldsChanged: value.allFieldIdsChanged.map(references.field),
    noFieldsChanged: value.noFieldIdsChanged.map(references.field),
  }
}

function toPersistedFieldChangeSettings(
  value: NotificationFieldChangeSettingsDraft | null
): NotificationFieldChangeSettingsType | null {
  if (!value) return null
  return {
    enabled: value.enabled,
    anyFieldIdsChanged: labeledValueStrings(value.anyFieldsChanged),
    allFieldIdsChanged: labeledValueStrings(value.allFieldsChanged),
    noFieldIdsChanged: labeledValueStrings(value.noFieldsChanged),
  }
}

function defaultDrafts(): TriggerSettingsDrafts {
  return {
    row_edited: null,
    advanced: {
      from: [],
      to: [],
      useAdvancedSettings: null,
    },
    reverted: {
      from: [],
      to: [],
      useAdvancedSettings: null,
    },
    option_state_changed: {},
  }
}

/**
 * Creates form drafts without sharing mutable state with the persisted
 * notification configuration.
 */
export function toFormValues(
  notification: {
    trigger: NotificationTriggerType
    triggerSettings: NotificationTriggerSettingsType | null
  } | null,
  referenceData: NotificationReferenceData
): TriggerSettingsFormValues {
  const drafts = defaultDrafts()
  const references = createNotificationReferenceValues(referenceData)
  if (!notification) return { drafts }

  const trigger = notification.trigger
  const settings = notification.triggerSettings
  if (!settings || settings.type !== trigger) return { trigger, drafts }

  switch (settings.type) {
    case 'row_edited':
      drafts.row_edited = copyFieldChangeSettings(settings, references)
      break
    case 'advanced':
    case 'reverted':
      drafts[settings.type] = {
        from: settings.from.map(references.state),
        to: settings.to.map(references.state),
        useAdvancedSettings: copyFieldChangeSettings(
          settings.useAdvancedSettings,
          references
        ),
      }
      break
    case 'option_state_changed':
      drafts.option_state_changed = {
        field: references.field(settings.fieldId),
      }
      break
  }

  return { trigger, drafts }
}

/**
 * Normalizes the selected trigger and its draft settings for form submission.
 *
 * `triggerSettings` uses the database JSON shape, while `trigger` preserves the
 * selected trigger when its settings are `null`. Empty transition selections
 * persist as empty arrays meaning any valid transition side. The function
 * returns `undefined` when required form values are missing or malformed.
 */
export function toSubmission({
  drafts,
  trigger,
}: TriggerSettingsFormValues): TriggerFormSubmission | undefined {
  if (!trigger) return undefined

  switch (trigger) {
    case 'row_edited': {
      const settings = toPersistedFieldChangeSettings(drafts.row_edited)
      return {
        trigger,
        triggerSettings: settings
          ? {
              type: trigger,
              ...settings,
            }
          : null,
      }
    }
    case 'row_comments_changed':
      return { trigger, triggerSettings: null }
    case 'advanced': {
      const draft = drafts.advanced
      const fromReferences = toNotificationStatusReferences(draft.from)
      const toReferences = toNotificationStatusReferences(draft.to)
      if (!fromReferences || !toReferences) return undefined
      const from = fromReferences.filter(
        (state): state is AdvancedFromState => state.type !== 'closed'
      )
      const to = toReferences.filter(
        (state): state is AdvancedToState =>
          state.type !== 'create' && state.type !== 'draft'
      )
      return {
        trigger,
        triggerSettings: {
          type: trigger,
          useAdvancedSettings: toPersistedFieldChangeSettings(
            draft.useAdvancedSettings
          ),
          from,
          to,
        },
      }
    }
    case 'reverted': {
      const draft = drafts.reverted
      const fromReferences = toNotificationStatusReferences(draft.from)
      const toReferences = toNotificationStatusReferences(draft.to)
      if (!fromReferences || !toReferences) return undefined
      const from = fromReferences.filter(
        (state): state is RevertedFromState =>
          state.type !== 'create' && state.type !== 'draft'
      )
      const to = toReferences.filter(
        (state): state is RevertedToState =>
          state.type !== 'create' && state.type !== 'closed'
      )
      return {
        trigger,
        triggerSettings: {
          type: trigger,
          useAdvancedSettings: toPersistedFieldChangeSettings(
            draft.useAdvancedSettings
          ),
          from,
          to,
        },
      }
    }
    case 'removed':
    case 'restored':
      return { trigger, triggerSettings: null }
    case 'option_state_changed': {
      const draft = drafts.option_state_changed
      const fieldId = labeledValueString(draft.field)
      if (!fieldId) return undefined

      return {
        trigger,
        triggerSettings: {
          type: trigger,
          fieldId,
        },
      }
    }
  }
}
